import {useOpenedStateManager} from '@aboutyou/opened-state-manager';
import {useCallback} from 'react';
import {
  SelectedSize,
  useSelectedSizeState,
} from '../SizeSelection/hooks/useSelectedSizeState';

export enum MiniAdpDialogOpenedStateKey {
  MAIN_MINI_ADP = 'wishlist-mini-adp',
  COUPON_WALLET_MAIN_MINI_ADP = 'coupon-wallet-main-mini-adp',
}

type OpenCallbackParams = {
  productId: number;
  filteredSizeId?: number;
  couponPromotionId?: string;
  recoType?: string;
  streamPosition?: number;
  locationId?: string;
  openFromBasketFlyout?: boolean;
};

function parseOpenedStateParameter(parameter: string | undefined) {
  const [
    productId,
    filteredSizeId,
    couponPromotionId,
    recoType,
    streamPosition,
    locationId,
    openFromBasketFlyout,
  ] = (parameter || '').split(':');

  return {
    productId,
    filteredSizeId,
    couponPromotionId,
    recoType,
    streamPosition,
    locationId,
    isOpenFromBasketFlyout: openFromBasketFlyout === '1',
  };
}

/**
 * Reads the mini ADP opened state straight from the history state instead of props,
 * because the page tracking context (TrackingProvider) has to report `basket_flyout`
 * for every event fired while a mini ADP opened from the basket flyout is on screen,
 * and it sits above the components that know about that dialog.
 */
export function useIsMiniAdpOpenFromBasketFlyout(): boolean {
  const mainMiniAdp = useOpenedStateManager(
    MiniAdpDialogOpenedStateKey.MAIN_MINI_ADP,
  );
  const couponWalletMiniAdp = useOpenedStateManager(
    MiniAdpDialogOpenedStateKey.COUPON_WALLET_MAIN_MINI_ADP,
  );

  return [mainMiniAdp, couponWalletMiniAdp].some(
    ({isOpen, parameter}) =>
      isOpen && parseOpenedStateParameter(parameter).isOpenFromBasketFlyout,
  );
}

export function useMiniAdpDialogOpenedState(
  key:
    | MiniAdpDialogOpenedStateKey
    | undefined = MiniAdpDialogOpenedStateKey.MAIN_MINI_ADP,
): {
  open: (params: OpenCallbackParams) => void;
  close: VoidFunction;
  selectedSize?: SelectedSize;
  setSelectedSize: (size: SelectedSize) => void;
  couponPromotionId?: string;
  recoType?: string;
  streamPosition?: number;
  locationId?: string;
} & (
  | {isOpen: true; productId: number; filteredSizeId?: number}
  | {isOpen: false; productId: undefined; filteredSizeId: undefined}
) {
  const {open, isOpen, cancel, parameter} = useOpenedStateManager(key);

  const {
    productId: productIdFromParam,
    filteredSizeId,
    couponPromotionId,
    recoType: recoTypeParam,
    streamPosition,
    locationId,
  } = parseOpenedStateParameter(parameter);
  const productId = Number(productIdFromParam);
  const trackingRecoType = recoTypeParam?.replace(/@/g, ':') || undefined;
  const streamPositionNumber = streamPosition
    ? Number(streamPosition)
    : undefined;
  const {selectedSize, setSelectedSize} = useSelectedSizeState(
    undefined,
    productId,
  );

  const openDialog = useCallback(
    ({
      productId,
      filteredSizeId,
      couponPromotionId,
      recoType,
      streamPosition,
      locationId,
      openFromBasketFlyout,
    }: OpenCallbackParams) => {
      open({
        parameter: [
          productId,
          filteredSizeId || '',
          couponPromotionId || '',
          recoType ? recoType.replace(':', '@') : '',
          streamPosition || '',
          locationId || '',
          openFromBasketFlyout ? 1 : '',
        ].join(':'),
      });
    },
    [open],
  );

  const closeDialog = useCallback(() => {
    setSelectedSize(undefined);
    cancel();
  }, [cancel, setSelectedSize]);

  if (!isOpen) {
    return {
      open: openDialog,
      close: closeDialog,
      selectedSize,
      setSelectedSize,
      isOpen,
      productId: undefined,
      filteredSizeId: undefined,
      recoType: trackingRecoType,
      locationId,
    };
  }

  return {
    open: openDialog,
    close: closeDialog,
    selectedSize,
    setSelectedSize,
    isOpen,
    productId,
    filteredSizeId: filteredSizeId ? Number(filteredSizeId) : undefined,
    couponPromotionId,
    recoType: trackingRecoType,
    streamPosition: streamPositionNumber,
    locationId,
  };
}
